import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Wallet,
  AlertCircle,
  CheckCircle,
  Clock,
  Calendar,
  Plus,
  Filter,
  Download,
  MoreVertical,
  MessageCircle,
  TrendingUp,
  FileText,
  Users,
  Settings
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { formatRupiah, formatPercent, generateWhatsAppLink, generatePaymentReminder } from '../utils/format';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Finance = () => {
  const [invoices, setInvoices] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoReminders, setAutoReminders] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [invoicesRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/invoices`),
        axios.get(`${API}/analytics/billing`)
      ]);
      
      setInvoices(invoicesRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Paid</Badge>;
      case 'overdue':
        return <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Overdue</Badge>;
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Pending</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const handleSendReminder = (invoice) => {
    const message = generatePaymentReminder(
      invoice.tenant_name,
      invoice.amount,
      invoice.due_date,
      invoice.unit_name
    );
    const whatsappLink = generateWhatsAppLink(invoice.phone, message);
    window.open(whatsappLink, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1A56DB]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="finance-page">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Billing Management
          </h1>
          <p className="text-slate-500 mt-1 max-w-xl">
            Monitor financial performance, automate tenant reminders, and manage accounts receivable across your portfolio.
          </p>
        </div>
        <div className="flex flex-col items-end gap-3">
          {/* Auto Reminder Toggle */}
          <div className="flex items-center gap-3 bg-slate-50 px-4 py-2 rounded-lg">
            <div className="text-right">
              <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Auto-Send WhatsApp Reminders</p>
              <p className="text-xs text-slate-500">Automated at 09:00 AM daily</p>
            </div>
            <Switch 
              checked={autoReminders} 
              onCheckedChange={setAutoReminders}
              data-testid="auto-reminders-toggle"
            />
          </div>
          <Button className="gap-2 bg-[#1A56DB] hover:bg-[#1E40AF]" data-testid="new-invoice-btn">
            <Plus className="w-4 h-4" />
            New Invoice
          </Button>
        </div>
      </div>

      {/* Metrics Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Collections */}
        <div className="metric-card" data-testid="metric-collections">
          <div className="flex items-start justify-between mb-3">
            <div className="w-10 h-10 bg-[#EFF6FF] rounded-lg flex items-center justify-center">
              <Wallet className="w-5 h-5 text-[#1A56DB]" />
            </div>
            <Badge variant="outline" className="text-green-600 bg-green-50 border-green-200">
              +{analytics?.collections_change}%
            </Badge>
          </div>
          <p className="text-sm text-slate-500 mb-1">Total Collections (MoM)</p>
          <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {formatRupiah(analytics?.total_collections)}
          </p>
          <div className="mt-2 h-1 bg-[#1A56DB] rounded-full" />
        </div>

        {/* Outstanding Receivables */}
        <div className="metric-card" data-testid="metric-outstanding">
          <div className="flex items-start justify-between mb-3">
            <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-red-500" />
            </div>
            <span className="text-xs text-red-500 font-medium">{analytics?.overdue_count} Overdue</span>
          </div>
          <p className="text-sm text-slate-500 mb-1">Outstanding Receivables</p>
          <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {formatRupiah(analytics?.outstanding_receivables)}
          </p>
          <div className="mt-2 h-1 bg-red-500 rounded-full" />
        </div>

        {/* Collection Efficiency */}
        <div className="metric-card" data-testid="metric-efficiency">
          <div className="flex items-start justify-between mb-3">
            <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">{analytics?.collection_efficiency}% Rate</span>
          </div>
          <p className="text-sm text-slate-500 mb-1">Collection Efficiency</p>
          <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {formatRupiah(analytics?.total_collections * (analytics?.collection_efficiency / 100))}
          </p>
          <div className="mt-2 h-1 bg-green-500 rounded-full" />
        </div>

        {/* Projected Revenue */}
        <div className="metric-card" data-testid="metric-projected">
          <div className="flex items-start justify-between mb-3">
            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
              <Calendar className="w-5 h-5 text-slate-600" />
            </div>
            <span className="text-xs text-slate-500">Next Batch: Jun 01</span>
          </div>
          <p className="text-sm text-slate-500 mb-1">Projected Revenue (Next Mo)</p>
          <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {formatRupiah(analytics?.projected_revenue)}
          </p>
          <div className="mt-2 h-1 bg-slate-300 rounded-full" />
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Invoices Table */}
        <div className="lg:col-span-2 chart-container">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Recent Invoices
            </h3>
            <div className="flex items-center gap-2">
              <Button variant="link" className="text-[#1A56DB] p-0" data-testid="export-csv-btn">
                Export CSV
              </Button>
              <Button variant="outline" size="sm" className="gap-2" data-testid="filters-btn">
                Filters
              </Button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Tenant & Unit</th>
                  <th>Due Date</th>
                  <th>Amount (Rp)</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => (
                  <tr key={invoice.id} data-testid={`invoice-row-${invoice.id}`}>
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#1A56DB] rounded-lg flex items-center justify-center text-white font-semibold text-sm">
                          {invoice.tenant_initials}
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">{invoice.tenant_name}</p>
                          <p className="text-xs text-slate-500">{invoice.property_name} - {invoice.unit_name}</p>
                        </div>
                      </div>
                    </td>
                    <td className="text-slate-600">{invoice.due_date}</td>
                    <td className={`font-medium ${invoice.status === 'overdue' ? 'text-red-600' : 'text-slate-900'}`}>
                      {formatRupiah(invoice.amount).replace('Rp ', '')}
                    </td>
                    <td>{getStatusBadge(invoice.status)}</td>
                    <td>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`invoice-actions-${invoice.id}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem>View Details</DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleSendReminder(invoice)}
                            data-testid={`send-whatsapp-${invoice.id}`}
                          >
                            <MessageCircle className="w-4 h-4 mr-2" />
                            Send WhatsApp Reminder
                          </DropdownMenuItem>
                          <DropdownMenuItem>Mark as Paid</DropdownMenuItem>
                          <DropdownMenuItem>Download Invoice</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Button variant="link" className="w-full mt-4 text-[#1A56DB]" data-testid="view-all-billing">
            View All Billing History
          </Button>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Automation Insights */}
          <div className="bg-[#1A56DB] rounded-xl p-6 text-white" data-testid="automation-insights">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-4 h-4" />
              </div>
              <h3 className="font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Automation Insights</h3>
            </div>
            <p className="text-blue-100 text-sm mb-4">
              WhatsApp reminders have accelerated your collection cycle by <span className="text-white font-semibold">4.2 days</span> this month.
            </p>
            <div className="space-y-3">
              <div className="flex items-center justify-between bg-white/10 rounded-lg px-4 py-3">
                <span className="text-blue-100 text-sm">Reminders Sent</span>
                <span className="font-bold">{analytics?.reminders_sent}</span>
              </div>
              <div className="flex items-center justify-between bg-white/10 rounded-lg px-4 py-3">
                <span className="text-blue-100 text-sm">Response Rate</span>
                <span className="font-bold">{analytics?.response_rate}%</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div className="chart-container">
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4">Portfolio Quick Links</h3>
            <div className="space-y-2">
              <Button variant="ghost" className="w-full justify-between text-slate-700" data-testid="generate-report-btn">
                <span className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Generate Monthly Report
                </span>
                <span className="text-slate-400">›</span>
              </Button>
              <Button variant="ghost" className="w-full justify-between text-slate-700" data-testid="manage-contacts-btn">
                <span className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Manage Payor Contacts
                </span>
                <span className="text-slate-400">›</span>
              </Button>
              <Button variant="ghost" className="w-full justify-between text-slate-700" data-testid="reminder-settings-btn">
                <span className="flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  Reminder Settings
                </span>
                <span className="text-slate-400">›</span>
              </Button>
            </div>
          </div>

          {/* Top Performing Asset */}
          <div className="relative rounded-xl overflow-hidden h-48" data-testid="top-asset">
            <img 
              src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=400&h=300&fit=crop" 
              alt="Top Asset"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-4">
              <p className="text-xs text-slate-300 uppercase tracking-wider">Top Performing Asset</p>
              <p className="text-white font-bold text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>Pakuwon Tower</p>
              <div className="mt-2 flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-white/30 rounded-full overflow-hidden">
                  <div className="h-full bg-green-400 rounded-full" style={{ width: '98%' }} />
                </div>
                <span className="text-white text-sm font-medium">98% Paid</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Finance;
